'use client';

import { c as _c } from "react/compiler-runtime";
import { ImageIcon } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { cn } from '@/lib/utils';
interface OptimizedScenarioImageProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: 'square' | 'video' | 'auto';
  showLoadingState?: boolean;
  onError?: () => void;
  /** Use group-hover instead of hover for scale effect (parent must have 'group' class) */
  groupHover?: boolean;
  /** Object-fit mode for the image */
  objectFit?: 'cover' | 'contain';
}

/** Optimized image component with lazy loading, error handling, and loading states */
export const OptimizedScenarioImage = t0 => {
  const $ = _c(27);
  const {
    src,
    alt,
    className,
    aspectRatio: t1,
    showLoadingState: t2,
    onError,
    groupHover: t3,
    objectFit: t4
  } = t0;
  const aspectRatio = t1 === undefined ? "square" : t1;
  const showLoadingState = t2 === undefined ? true : t2;
  const groupHover = t3 === undefined ? false : t3;
  const objectFit = t4 === undefined ? "cover" : t4;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  let handleError;
  let t5;
  let t6;
  if ($[0] !== aspectRatio || $[1] !== className || $[2] !== hasError || $[3] !== onError) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const aspectClass = {
        square: "aspect-square",
        video: "aspect-video",
        auto: ""
      }[aspectRatio];
      let t7;
      if ($[7] !== onError) {
        t7 = () => {
          setHasError(true);
          onError?.();
        };
        $[7] = onError;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      handleError = t7;
      if (hasError) {
        let t8;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <ImageIcon className="h-8 w-8 text-muted-foreground" />;
          $[9] = t8;
        } else {
          t8 = $[9];
        }
        t6 = <div className={cn("flex items-center justify-center bg-muted", aspectClass, className)}>{t8}</div>;
        break bb0;
      }
      t5 = cn("relative overflow-hidden", aspectClass, className);
    }
    $[0] = aspectRatio;
    $[1] = className;
    $[2] = hasError;
    $[3] = onError;
    $[4] = handleError;
    $[5] = t5;
    $[6] = t6;
  } else {
    handleError = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[10] !== isLoaded || $[11] !== showLoadingState) {
    t7 = showLoadingState && !isLoaded && <div className="absolute inset-0 bg-muted animate-pulse" />;
    $[10] = isLoaded;
    $[11] = showLoadingState;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = objectFit === "contain" ? "object-contain h-full w-full" : "object-cover h-full w-full";
  const t9 = groupHover ? "group-hover:scale-105" : "hover:scale-105";
  const t10 = isLoaded ? "opacity-100" : "opacity-0";
  let t11;
  if ($[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
    t11 = cn("transition-transform duration-200 will-change-transform", t8, t9, t10);
    $[13] = t10;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsLoaded(true);
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== alt || $[19] !== handleError || $[20] !== src || $[21] !== t11) {
    t13 = <Image src={src} alt={alt} fill={true} unoptimized={true} sizes="100vw" className={t11} onLoad={t12} onError={handleError} />;
    $[18] = alt;
    $[19] = handleError;
    $[20] = src;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t5 || $[25] !== t7) {
    t14 = <div className={t5}>{t7}{t13}</div>;
    $[23] = t13;
    $[24] = t5;
    $[25] = t7;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
};
OptimizedScenarioImage.displayName = 'OptimizedScenarioImage';